<template>
	<q-page padding>
		<h2 class="text-center">Colour Management</h2>
		<h4>Edit Colour</h4>
		<q-card>
			<div class="row" v-if="loaded">
				<div class="q-pa-md">
					<div class="q-gutter-y-md column" >
						<div class="row" style="height: 40px; line-height: 40px; text-align: center; ">
							<div class="text-bold" style="width:150px">Colour Name:</div>
							<q-input outlined v-model="colourName" dense style="width:200px" />
						</div>
						<div class="row" style="height: 44px; line-height: 44px; text-align: center; ">
							<div class="text-bold" style="width:150px">SuperGroup:</div>
							<q-select outlined v-model="supergroup" :options="superGroupList" @input="group=''"  dense style="width:200px"/>
						</div>        
						<div class="row" style="height: 44px; line-height: 44px; text-align: center;" >
							<div class="text-bold" style="width:150px">Group:</div>
							<q-select outlined v-model="group" :options="generateGroupColourList(supergroup)" dense style="width:200px"/>
						</div>
						<div class="row" style="line-height: 44px; text-align: center; ">
							<div class="text-bold" style="width:150px">Hex:</div>
							<q-input :rules="['anyColor']" outlined v-model="hex" dense style="width:200px">
								<template v-slot:append>
									<div style="min-width: 25px; height: 25px; border:1px solid silver" :style="{'backgroundColor':hex}">              
									</div>
								</template>
							</q-input>
						</div>
						<div>
							<q-color format-model="hex" v-model="hex" no-header style="margin-left: 150px; display:block; width:200px" class="column"/>
						</div>            
					</div>
				</div> 
				<div class="q-pa-md">
					<div class="q-gutter-y-md column">
						<div class="row" style="height: 40px; line-height: 40px; text-align: center; ">
							<div class="text-bold" style="width:150px">Manufacturer:</div>
							{{manufacturer}}
						</div>
						<div class="row" style="line-height: 44px; text-align: center;" >
							<div class="text-bold" style="width:150px">Brands:</div>
							{{brands.join(', ')}}
						</div>
						<div class="row" style="line-height: 44px; text-align: center; ">
							<div class="text-bold" style="width:150px">Materials:</div>
							<div class="column">
								<div class="row" v-for="(material,index) in materialsList" :key="index">
									<q-checkbox v-model="materialsSelected" :val="material"  :label="material" />
								</div>
							</div>          
						</div>
						<div class="row" style="line-height: 44px; text-align: center; ">
							<div class="text-bold" style="width:150px">Notes:</div>
							<div style="width: 500px">
								<q-input v-model="notes" outlined type="textarea" /> 
							</div>
						</div>
						<div class="row" style="line-height: 44px; text-align: center; ">
							<div class="text-bold" style="width:150px">Last Update:</div>
							{{formatDateTime(updatedAt)}} 	({{fromNow(updatedAt)}})
						</div>   					               
					</div>   
				</div>      
			</div>  
			<div class="text-center" v-if="loaded" >
				<q-btn accesskey="s" style="margin:30px" @click="updateColour" color="primary">Update</q-btn>
				<q-btn label="Move" style="margin:30px" icon-right="swap_horiz" @click="replaceColourDialog()" v-if="safeToDelete" />
				<span>		
					<q-btn label="Delete" icon-right="delete" style="margin:30px" @click="deleteColour()" :disable="safeToDelete" />
					<q-tooltip v-if="safeToDelete" transition-show="scale" transition-hide="scale" content-style="color:black; background: white; border: solid 1px black" >
					A colour can't be deleted if there are skus associated to it
					</q-tooltip>
				</span>									
			</div>
		</q-card>
		<br>	
		<q-table v-if="productsLoaded"
			:data="products"
			:columns="columns()"
			separator="none"
			dense
			:rows-per-page-options="[10, 20, 50]"
			binary-state-sort
			row-key="id" >
			<template v-slot:top="props">
				<div class="row justify-end">
					<div class="text-h6">Products using this colour: <strong>{{products.length}}</strong></div>
				</div>
			</template>  
			<template v-slot:body-cell-img="props">
				<q-td :props="props">
					<q-img dense :src="((props.row.images[0] || {}).url) || ''" style="width: 25px; height: 25px;" contain />
				</q-td>
			</template>
			<template v-slot:body-cell-name="props">
				<q-td key="name" :props="props"> <router-link :to="`/admin/products/${props.row.id}`">{{ props.row.name }}</router-link></q-td>
			</template>
		</q-table>
		<q-dialog v-model="showMoveCard">
			<q-card class="my-card" style="min-width: 650px">
				<q-card-section>
					<div class="row no-wrap items-center">
						<div class="col text-h6">
							Replace colour
						</div>
					</div>
					<div style="color:red; padding: 10px; border: dashed 1px; margin:10px " ><q-icon name="warning" color="red" size="sm"/>This tool will replace the current colour <strong>({{colourName}})</strong> on every product sku that uses this colour to another existing colour from the dropdown list below.</div>
				</q-card-section>
				<q-card-section>
					<div class="row">
						<div class="text-bold">Replace:</div>
						<div :style="{border: '1px silver solid', backgroundColor: hex, width: '20px',height: '20px', margin:'0 10px'}"></div>
						<div>{{loadedColour.supergroup}} ➜ {{loadedColour.group}} ➜ {{loadedColour.name}}</div>
					</div>
				</q-card-section>	
				<q-card-section class="q-pt-none">
					<div class="row">
						<div style="line-height: 44px;" class="text-bold">With:</div>
						<div style="width: 550px; margin-left:10px; margin-bottom:30px">
							<q-select outlined options-dense dense :options="coloursToMoveList" v-model="colourToMove">
								<template v-slot:no-option>
									<q-item>
										<q-item-section class="text-grey"> No colours available </q-item-section>
									</q-item>
								</template>
								<template v-slot:option="scope" >
									<template v-if="scope.opt.id != colourID">									
										<q-item v-bind="scope.itemProps" v-on="scope.itemEvents" >
											<q-item-section side>
												<div :style="{border: '1px silver solid', backgroundColor: scope.opt.hex, width: '20px',height: '20px'}"></div>
											</q-item-section>
											<q-item-section>
												<q-item-label>{{scope.opt.supergroup}} ➜ {{scope.opt.group}} ➜ {{scope.opt.name}} ({{scope.opt.materials.join(' - ')}})</q-item-label>
											</q-item-section>
										</q-item>
									</template>									
								</template>
								<template v-slot:selected-item="scope">
									<div class="row">
										<template v-if="typeof scope.opt == 'string'">
											<div>Select a colour</div>
										</template>
										<template v-else>
											<div :style="{'border': 'solid silver 1px', 'margin-top': '3px', 'margin-right':'5px', 'max-height':'15px', 'min-width': '15px', 'background-color':scope.opt.hex}"></div>
											<div>{{scope.opt.supergroup}} ➜ {{scope.opt.group}} ➜ {{scope.opt.name}} ({{scope.opt.materials.join(' - ')}})</div>
										</template>
									</div>
								</template>
							</q-select>
						</div>
					</div>
				</q-card-section>
				<q-separator />
				<q-card-actions align="right">
				<q-btn v-close-popup flat color="black" label="Cancel" />
				<q-btn v-close-popup flat color="primary" label="Apply" @click="replaceColourById()" :disable="typeof colourToMove == 'string'" />
				</q-card-actions>
			</q-card>
		</q-dialog>		
	</q-page>
</template>

<script>
import gql from "graphql-tag";
import { formatDateTime, formatDate, formatMonth, fromNow } from '../../util/string'
import _ from 'lodash';
import notifyMessage from '../../util/notifyMessage'
import notifyError from '../../util/adminNotifyError'

export default {
  name: 'ColoursTable',
  data(){
    return{
      colourID: this.$route.params.id,
      colourName: "",
      coloursToMoveList: [],
      colourToMove: "Select a colour",
      group: "",
      hex: "",
      loaded: false,
      loadedColour: "",
      manufacturer: "",
      materialsList: [ "PE", "PU", "Fiberglass", "Wood" ],
      materialsSelected: [],
      notes: "",
      products: [],
      productsLoaded: false,
      showMoveCard: false,
      supergroup: "",
      superGroupList: [ "White", "Black", "Red", "Orange", "Green", "Purple", "Blue", "Yellow", "Teal", "Pink", "Grey", "Custom" ],
      updatedAt: ""
    }
  },
	computed:{
		safeToDelete(){
			return this.products.length == 0 ? false : true
		},
	},
  methods:{
		columns (){
			return [
				{ name: 'img', field: row => row.sku?.product?.images?.[0]?.url },           
				{ name: 'name', label: 'Name', field: row => row.name || 'Not Found', sortable: true, align: 'left' },
				{ name: 'reference', label: 'Reference', field: row => row.reference, sortable: true, align: 'left' },
				{ name: 'manufacturer', label: 'Manufacturer', field: row => row.attributes?.manufacturer, sortable: true, align: 'left' },
				{ name: 'brand', label: 'Brand', field: row => row.attributes?.brand, sortable: true, align: 'left' },
				{ name: 'type', label: 'Type', field: row => row.attributes?.type, sortable: true, align: 'left' },			
				{ name: 'material', label: 'Material', field: row => row.attributes?.material, sortable: true, align: 'left' },			
				{ name: 'range', label: 'Range', field: row => row.attributes?.range, sortable: true, align: 'left' },
				{ name: 'size', label: 'Size', field: row => row.attributes?.size, sortable: true },
				{ name: 'grip', label: 'Grip', field: row => row.attributes?.grip, sortable: true },
				{ name: 'holds', label: 'Holds', field: row => Number(row.attributes?.holds), sortable: true, align: 'right' },
			]
		},
    async deleteColour(){
      this.$q.dialog({
        style:{'minWidth':'600px'},
        title: 'Delete Colour',
        message: `
          <div class="row" style="margin-left:20px">
            <div style="border: 1px silver solid; background-color: ${this.loadedColour.hex}; width: 20px; height: 20px; margin:30px}"></div>
            <div style="margin-left:10px">${this.loadedColour.supergroup} ➜ ${this.loadedColour.group} ➜ ${this.loadedColour.name} ?</div>
          </div>
        `,
        html: true,
        cancel: true,
        ok: "Delete"
      }).onOk(async(value) => {
        try{
          await this.deleteColour_query(this.colourID)
          notifyMessage(this.$q,"Colour Deleted","Successfully");
          this.$router.push("/admin/coloursTable")
        }catch(error){
          notifyError(this.$q,"Error deleting colour",error)
        } 
      })
    },
    formatDateTime: formatDateTime,
  	fromNow: fromNow,
    generateGroupColourList(SuperGroupColour){
      let groupList = []
      if(SuperGroupColour){
        groupList = ["Light","","Dark","Bright","Fluoro"]
        for(let i = 0; i<groupList.length; i++){
          let group = groupList[i] + " " + SuperGroupColour;
          groupList[i] = group.trim()
        }
      }
      return groupList
    },
		async loadColour(){
      try{
        this.loaded = false;
        this.loadedColour = await this.getColourById_query(this.colourID);
        this.colourName = this.loadedColour.name
        this.supergroup = this.loadedColour.supergroup
        this.group = this.loadedColour.group
        this.hex = this.loadedColour.hex
        this.manufacturer = this.loadedColour.manufacturer
        this.brands = _.cloneDeep(this.loadedColour.brands)
        this.materialsSelected = _.cloneDeep(this.loadedColour.materials)
        this.notes = this.loadedColour.notes
        this.updatedAt =  this.loadedColour.updatedAt
        this.loaded = true;
      }catch(error){
        notifyError(this.$q,"Error loading colour",error)
      }
		},    		    
    async productsByColourId(){
      try{
				this.products = await this.productsByColourId_query(this.colourID)
				this.productsLoaded = true 
      }catch(error){
        notifyError(this.$q,"Error getting products using id",error)
      }
    },
    async replaceColourById(){
			let from = this.colourID
			let to = this.colourToMove.id
      try{
        await this.replaceColourById_query(from,to);
        await this.productsByColourId()
			  notifyMessage(this.$q,"Colour Replaced","All skus were updated");
      }catch(error){
        notifyError(this.$q,"Error replacing colour",error)
      }
    },
		async replaceColourDialog(){
      try{
				this.coloursToMoveList = await this.coloursByManufacturer_query(this.manufacturer);
				this.showMoveCard = true
      }catch(error){
        notifyError(this.$q,"Error",error)
      }
		},
		async updateColour(){
      try{
			  let input = {}
        if(this.colourName != this.loadedColour.name) input.name = this.colourName
        if(this.supergroup != this.loadedColour.supergroup) input.supergroup = this.supergroup
        if(this.group != this.loadedColour.group) input.group = this.group
        if(this.notes != this.loadedColour.notes) input.notes = this.notes
        if(this.hex != this.loadedColour.hex) input.hex = this.hex
        if(!_.isEqual(_.sortBy(this.materialsSelected), _.sortBy(this.loadedColour.materials))) input.materials = _.cloneDeep(this.materialsSelected)
        if(input && Object.keys(input).length > 0	){
          await this.editColour_query(this.colourID,input)
          notifyMessage(this.$q,"Colour Updated","Colour details were updated!");
          await this.loadColour()
        }
      }catch(error){
        notifyError(this.$q,"Error updating colour",error)
      }		
		},
  // QUERY FUNCTIONS:
		async coloursByManufacturer_query(manufacturer){
      try {
        const result = await this.$apollo.query({
          query: gql`query coloursByManufacturer($manufacturer: String!) {
            coloursByManufacturer (manufacturer:$manufacturer){
              id
              name
              supergroup
              group
              hex
              manufacturer
              brands
              materials
              notes
						}
          }
        `,
          variables: {  
						manufacturer:manufacturer
          }
        })
        return result.data.coloursByManufacturer;
      } catch (error) {
        console.log("coloursByManufacturer_query Error:", error)
        throw new Error(error);    
      }   					
		},
    async deleteColour_query(colourID){
			try {
				const result = await this.$apollo.mutate({
					mutation: gql`mutation deleteColour ($colourId: ID!) {
						deleteColour(colourId: $colourId)
					}
				`,
					variables: {
						colourId: colourID,
					}
				})
				return result.data.deleteColour;
			} catch (error) {
        console.log('deleteColour_query Error:',error);
        throw new Error(error);    
			}			
		},
		async editColour_query(colourID,input) {
			try {
				const result = await this.$apollo.mutate({
					mutation: gql`mutation editColour ($id: ID!, $input: ColourInput!) {
						editColour(id: $id, input: $input) {
							id, 
							name, 
							supergroup, 
							group, 
							hex, 
							manufacturer, 
							brands, 
							materials, 
							notes,
							createdAt,
							updatedAt 						
						}
					}
				`,
					variables: {
						id: colourID,
						input: input
					}
				})
				return result.data.editColour;
			} catch (error) {
				console.log("editColour_query Error:", error)
        throw new Error(error);    
			}
		},
	async getColourById_query(id){
      try {
        const result = await this.$apollo.query({
          query: gql`
					query colourById ($id: ID!) { 
						colourById (id: $id) { 
							id, 
							name, 
							supergroup, 
							group, 
							hex, 
							manufacturer, 
							brands, 
							materials, 
							notes,
							createdAt,
							updatedAt 
							} 
						} 
					`,
					variables: {
						id: id
					}
        })			
        return result.data.colourById;
      } catch (error) {
        console.log("getColourById_query Error:", error)
        throw new Error(error);    
      }      
    },	    
    async productsByColourId_query(colourID){
      try {
        const result = await this.$apollo.query({
          query: gql`query productsByColourId($id: ID!) {
            productsByColourId (id:$id){
              id
              name
              description
              reference
              images {
                url
                name
              }
              attributes
              urlSlug
              skus {
                id
                colour
                attributes
                barcode
                options
                price
                containerPrice
                retailPrice
                showInContainer
                showInRetail
                hideInStock
                stock
              }
            }
          }
        `,
          variables: {  
            id: colourID
          },
					fetchPolicy: 'network-only',
        })
        return result.data.productsByColourId;
      } catch (error) {
        console.log("productsByColourId_query Error:", error)
        throw new Error(error);    
      }      
    },    
    async replaceColourById_query(from,to){
      try {
        const result = await this.$apollo.mutate({
          mutation: gql`mutation replaceColourById($fromId: ID!,$toId: ID!) {
            replaceColourById (fromId:$fromId,toId:$toId){
							id
						}
          }
        `,
          variables: {  
            fromId: from,
						toId: to
          }
        })
        return result.data.replaceColourById;
      } catch (error) {
        console.log("ReplaceColourByID Error:", error)
        throw new Error(error);    
      }      
    }
  },
	async created(){
		await this.loadColour()
		await this.productsByColourId()
	},
}
</script>